import { StatsEventPlatform } from '@yandex-360/telemost-sdk/models/stats';
import { all, call, put, select, take } from 'typed-redux-saga';

import { getMyUserId, getRoomId, selectOrganizationId, selectSessionId } from '../../../../pages/call/selectors';
import { NotificationId } from '../../../../pages/call/types';
import { FEEDBACK_COUNT_STORAGE_KEY } from '../../../constants';
import { createLogger } from '../../../helpers/loggerFacade';
import { countMetrika } from '../../../helpers/metrika';
import Storage from '../../../helpers/storage';
import { cloudApi } from '../../../helpers/transport';
import { Config } from '../../config';
import { getAppVersionSelector, isMessengerIntegration } from '../../config/selectors';
import { NOTIFICATIONS_MAP, notify } from '../../notifications/ps-notifications';
import { isB2bUserSelector } from '../../user/selectors';
import { Feedback, FeedbackSubmission, setFeedbackComponentVisibility, submitFeedback } from '../slice';

interface FeedbackMeta {
    room_id: string;
    peer_id: string;
    organization_id: string | null;
    session_id: string | null;
    platform: StatsEventPlatform;
    version: Config['appVersion'];
    is_user_b2b: boolean;
}

const logger = createLogger('feedback');

const safeSendFeedback = (feedback: Feedback, meta: FeedbackMeta) => {
    return cloudApi.sendFeedback({
        ...meta,

        rating: feedback.rating,
        reasons: feedback.reasons || [],
        comment: feedback.comment,
    });
};

const FEEDBACK_PERIOD = 5;

function feedbackWasShown(meta: FeedbackMeta) {
    return cloudApi.feedbackWasShown({ ...meta });
}

function feedbackWasCreated(feedback: Feedback | FeedbackSubmission): feedback is Feedback {
    return feedback.rating !== undefined;
}

export const feedbackSaga = function* () {
    try {
        const peerId = yield* select(getMyUserId);
        const roomId = yield* select(getRoomId);

        const organizationId = yield* select(selectOrganizationId);
        const sessionId = yield* select(selectSessionId);

        if (!peerId || !roomId) {
            return;
        }

        const storageFeedbackValue = yield* call([Storage, 'getItem'], FEEDBACK_COUNT_STORAGE_KEY);
        let feedbackCount = parseInt(storageFeedbackValue || '0', 10) || FEEDBACK_PERIOD;

        if (feedbackCount < FEEDBACK_PERIOD) {
            feedbackCount++;
            Storage.setItem(FEEDBACK_COUNT_STORAGE_KEY, feedbackCount.toString());
            return;
        }

        Storage.setItem(FEEDBACK_COUNT_STORAGE_KEY, '1');
        yield put(setFeedbackComponentVisibility(true));

        const platform = (yield* select(isMessengerIntegration)) ? 'web-messenger' : 'web';
        const version = yield* select(getAppVersionSelector);
        const isUserB2b = yield* select(isB2bUserSelector);

        const meta = {
            room_id: roomId,
            peer_id: peerId,
            organization_id: organizationId,
            session_id: sessionId,
            platform: platform,
            version: version,
            is_user_b2b: isUserB2b,
        } as const;

        yield* call(feedbackWasShown, meta);

        const { payload } = yield* take(submitFeedback);

        if (feedbackWasCreated(payload)) {
            yield all([call(countMetrika, 'rate', payload.rating, roomId), call(safeSendFeedback, payload, meta)]);

            if (payload.rating <= 3) {
                yield put(notify(NOTIFICATIONS_MAP[NotificationId.FEEDBACK]()));
            }
        } else {
            countMetrika('rate', 'close');
        }
    } catch (error) {
        logger.error(error, { method: 'feedbackSaga' });
    } finally {
        yield put(setFeedbackComponentVisibility(false));
    }
};
